/**
 * FND-13 · Workspace Smart Side Panel
 *
 * Rail destro 320px collassabile, presente ≥ 1280px.
 * 5 tab "canonici" della Suite: AI · Anomalie · Timeline · Note · Cronologia
 * (il modulo può passare un sottoinsieme). Ogni tab ha un content-slot.
 *
 * L'AI content è sempre "disabled with pill Presto" finché il modulo AI non è attivo.
 *
 * Riutilizzabile: 90% dei moduli. Sprint V3.0B.
 *
 * Props:
 *   tabs         - array<{ key, label, content, badge?, aiPending? }>
 *   defaultTab   - key del tab iniziale
 *   collapsible  - true (default) mostra bottone comprimi
 *   defaultCollapsed - default false
 *   testid       - data-testid del wrapper
 */
import React from "react";
import { ChevronRight, ChevronLeft, Sparkles, AlertTriangle, Clock, FileText, History } from "lucide-react";

const TAB_ICONS = {
  ai: Sparkles,
  anomalies: AlertTriangle,
  timeline: Clock,
  notes: FileText,
  history: History,
};

export function WorkspaceSidePanel({
  tabs = [],
  defaultTab,
  collapsible = true,
  defaultCollapsed = false,
  testid = "workspace-side-panel",
}) {
  const [activeKey, setActiveKey] = React.useState(defaultTab || tabs[0]?.key);
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  const active = tabs.find((t) => t.key === activeKey) || tabs[0];

  if (!tabs.length) return null;

  if (collapsed) {
    return (
      <aside
        className="hidden xl:flex flex-col w-10 shrink-0 border-l border-suite-neutral-200 bg-white py-2 gap-1"
        data-testid={testid}
        data-collapsed="true"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="h-8 w-8 rounded mx-auto text-suite-neutral-500 hover:bg-suite-neutral-100 flex items-center justify-center"
          title="Espandi pannello"
          data-testid={`${testid}-expand`}
        >
          <ChevronLeft size={14} />
        </button>
        {tabs.map((t) => {
          const Icon = TAB_ICONS[t.key] || Sparkles;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => { setActiveKey(t.key); setCollapsed(false); }}
              className={`h-8 w-8 rounded mx-auto flex items-center justify-center transition-colors ${
                activeKey === t.key
                  ? "bg-suite-primary/10 text-suite-primary"
                  : "text-suite-neutral-500 hover:bg-suite-neutral-100"
              }`}
              title={t.label}
              data-testid={`${testid}-collapsed-tab-${t.key}`}
            >
              <Icon size={14} />
            </button>
          );
        })}
      </aside>
    );
  }

  return (
    <aside
      className="hidden xl:flex flex-col w-[320px] shrink-0 border-l border-suite-neutral-200 bg-white"
      data-testid={testid}
      data-collapsed="false"
    >
      <div className="flex items-center gap-0.5 px-2 pt-2 border-b border-suite-neutral-200">
        {tabs.map((t) => {
          const Icon = TAB_ICONS[t.key] || Sparkles;
          const isActive = activeKey === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveKey(t.key)}
              className={`h-8 px-2 text-[11.5px] font-semibold flex items-center gap-1 transition-colors ${
                isActive
                  ? "text-suite-primary border-b-2 border-suite-primary"
                  : "text-suite-neutral-500 hover:text-suite-neutral-700 border-b-2 border-transparent"
              }`}
              data-testid={`${testid}-tab-${t.key}`}
            >
              <Icon size={12} /> {t.label}
              {t.aiPending && (
                <span
                  className="text-[8.5px] font-bold tracking-widest uppercase px-1 py-0.5 rounded bg-purple-50 text-purple-600"
                  style={{ fontFamily: "Manrope" }}
                >
                  Presto
                </span>
              )}
              {typeof t.badge === "number" && t.badge > 0 && (
                <span className="text-[9.5px] font-bold px-1.5 rounded-full bg-amber-100 text-amber-700">
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
        {collapsible && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="ml-auto h-8 w-8 rounded text-suite-neutral-400 hover:bg-suite-neutral-100 flex items-center justify-center"
            title="Comprimi pannello"
            data-testid={`${testid}-collapse`}
          >
            <ChevronRight size={14} />
          </button>
        )}
      </div>
      <div
        className="flex-1 overflow-y-auto p-4 space-y-3"
        data-testid={`${testid}-content-${active?.key}`}
      >
        {active?.content}
      </div>
    </aside>
  );
}

/** AI Placeholder — box gradient viola/verde, sempre disabled con pill "Presto".
 *  Riutilizzabile in qualsiasi Side Panel di modulo. */
export function AIAssistantPlaceholder({ moduleHint, testid = "ai-placeholder" }) {
  return (
    <div
      className="rounded-xl border border-purple-200/60 bg-gradient-to-b from-purple-50/60 to-white p-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <Sparkles size={15} className="text-purple-600" />
        </div>
        <div className="leading-tight">
          <div className="text-[12.5px] font-semibold text-purple-700" style={{ fontFamily: "Manrope" }}>
            GI AI Assistant
          </div>
          <div className="text-[10.5px] text-suite-neutral-500">Disponibile con il modulo Suite AI</div>
        </div>
      </div>
      {moduleHint && (
        <div className="mt-2.5 rounded-lg bg-white border border-suite-neutral-200 p-2.5 text-[11.5px] text-suite-neutral-600 leading-snug">
          <span className="text-purple-600 font-semibold">Presto:</span> {moduleHint}
        </div>
      )}
      <button
        aria-disabled="true"
        className="mt-2.5 h-8 w-full rounded-lg bg-purple-100/70 text-purple-500 text-[11.5px] font-semibold cursor-not-allowed"
        data-testid={`${testid}-cta`}
      >
        Chiedi a GI AI · Presto
      </button>
    </div>
  );
}
